import * as z from "zod";
import dayjs from "dayjs";
import { validateUSPhoneNumber, validateOTP } from "../utils/phoneValidation";

export const emailValidationSchema = z.object({
  EMAIL_ADDRESS: z
    .string()
    .min(1, "Email is required")
    .email("Invalid Email Address"),
});

export const mobileOtpValidationSchema = z.object({
  MOBILE_OTP: z
    .string()
    .min(1, "Verification code is required")
    .refine((val) => validateOTP(val), {
      message: "Verification code must be 6 digits",
    }),
});

// Define your schemas
export const signupFirstStepSchema = z.object({
  FIRST_NAME: z.string().min(1, { message: "First Name is Required" }),
  LAST_NAME: z.string().min(1, { message: "Last Name is Required" }),
  EMAIL_ADDRESS: z
    .string()
    .min(1, "Email is required")
    .email("Invalid Email Address"),
});
export const addCollegueSchema = z.object({
  firstName: z.string().min(1, { message: "First Name is Required" }),
  lastName: z.string().min(1, { message: "Last Name is Required" }),
  emailAddress: z
    .string()
    .min(1, "Email is required")
    .email("Invalid Email Address"),
});

export const signupSecondStepSchema = z.object({
  PHONE_NUMBER: z
    .string()
    .min(1, { message: "Phone Number is Required" })
    .refine((val) => validateUSPhoneNumber(val), {
      message: "Invalid US phone number. Please enter a valid 10-digit US phone number or include country code.",
    }),
});

export const signupOtpStepSchema = z.object({
  otp: z.string().refine((val) => validateOTP(val), {
    message: "Verification code must be 6 digits",
  }),
});
export const loginValideSchema = z.object({
  EMAIL_ADDRESS: z
    .string()
    .min(1, "Email Address is required")
    .email("Invalid Email Address"),
  PASSWORD: z
    .string()
    .min(1, "Password is required")
    .min(7, "Password should be more than 6 characters")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[a-z]/, "Password must contain at least one lowercase letter")
    .regex(/\d/, "Password must contain at least one digit")
    .regex(/[^a-zA-Z0-9]/, "Password must contain at least one special symbol"),
});

export const passwordSchema = z
  .object({
    PASSWORD: z
      .string()
      .min(7, "Password should be more than 6 characters")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/[a-z]/, "Password must contain at least one lowercase letter")
      .regex(/\d/, "Password must contain at least one digit")
      .regex(
        /[^a-zA-Z0-9]/,
        "Password must contain at least one special symbol",
      ),
    confirmPassword: z.string(),
  })
  .refine((data) => data.PASSWORD === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });
export const promotionSourceSchema = z.object({
  INFLUENCER_SOURCE: z.string().min(1, "Please select an Option"),
});
export const jobRoleOnBoardSchema = z.object({
  JOBROLE_ID: z.string().min(1, "Job Role is required"),
});
export const jobDescriptionSchema = z.object({
  JOB_DESCRIPION: z.string().min(1, "Job Description is required"),
});
export const jobResponsibilitySchema = z.object({
  JOB_RESPONSIBILITY: z.string().min(1, "Job Responsibility is required"),
});

export const SpecialtyListOnBoardSchema = z.object({
  SPECIALTY_LIST: z
    .array(
      z.object({
        id: z.string().uuid(), // Ensure the id is a valid UUID (adjust if needed)
        name: z.string().min(1, "Specialty must not be empty"), // Ensure the name is a non-empty string
      }),
    )
    .min(1, "At least one specialty must be selected"),
  WEBSITE_NAME: z.string().optional(),
});

export const NursingLincenseSchema = z
  .object({
    LICENSE_STATE: z.string().min(1, "State is required"),
    LICENSE_TYPE: z.string().min(1, "License Type is required"),
    LICENSE_NUMBER: z.string().min(1, "License Number is required"),
    LICENSE_ISSUE_DATE: z.union([z.date(), z.literal(null)]).refine(
      (date) => {
        if (date === null) return false; // If null, return false for the "required" message
        return !isNaN(date.getTime()); // Validate if the date is not invalid
      },
      {
        message: "Issue Date is required",
      },
    ),
    LICENSE_EXPIRE_DATE: z.union([z.date(), z.literal(null)]).refine(
      (date) => {
        if (date === null) return false; // If null, return false for the "required" message
        return !isNaN(date.getTime()); // Validate if the date is not invalid
      },
      {
        message: "Expiration Date is required",
      },
    ),
  })
  .refine(
    (data) => {
      // Check if both dates are not the same
      if (data.LICENSE_ISSUE_DATE && data.LICENSE_EXPIRE_DATE) {
        return (
          data.LICENSE_ISSUE_DATE.getTime() !==
          data.LICENSE_EXPIRE_DATE.getTime()
        );
      }
      return true; // If one or both dates are null, skip this check
    },
    {
      message: "Issue Date and Expiration Date must not be the same",
      path: ["CROSS_FIELD_ERROR"],
    },
  );

export const UploadResumeSchema = z.object({
  FILE: z
    .instanceof(File)
    .refine((file) => file.size <= 5 * 1024 * 1024, {
      message: "File size must be less than 10MB",
    })
    .refine(
      (file) =>
        ["application/pdf", "image/jpeg", "image/png"].includes(file.type),
      {
        message: "Only PDF, JPEG, and PNG files are allowed",
      },
    ),
});

export const PreferredWorkAreaSchema = z.object({
  PREFERRED_AREA_OF_WORK: z
    .array(z.string())
    .min(1, "At least one work area must be selected"),
});

//Both OneTimeSchema and RecurringSchema not used by 17 Dec a new schema created ~ Gourav
export const OneTimeSchema = z.object({
  PAY_AMOUNT_FROM: z.string().min(1, { message: "From is Required" }),
  PAY_AMOUNT_TO: z.string().min(1, { message: "To is Required" }),
  REQUIRED_APPLICANTS: z.string().optional(),
  SERVICE_DATE: z.union([z.date(), z.literal(null)]).refine(
    (date) => {
      if (date === null) return false;
      return !isNaN(date.getTime());
    },
    { message: "Service Date is required" },
  ),
  SERVICE_TYPE: z.string().min(1, { message: "To is Required" }),
  JOB_IN_TIME: z.string().min(1, { message: "From Date is Required" }),
  JOB_OUT_TIME: z.string().min(1, { message: "To Date is Required" }),

  // JOB_IN_TIME: z.union([z.date(), z.literal(null)]).refine(date => {
  //     if (date === null) return false;
  //     return !isNaN(date.getTime());
  // // }, { message: "From Time is required" }),
  // JOB_OUT_TIME: z.union([z.date(), z.literal(null)]).refine(date => {
  //     if (date === null) return false;
  //     return !isNaN(date.getTime());
  // }, { message: "To Time is required" }),
});

export const RecusrringSchema = z.object({
  PAY_AMOUNT_FROM: z.string().min(1, { message: "From is Required" }),
  PAY_AMOUNT_TO: z.string().min(1, { message: "To is Required" }),

  SERVICE_DATE: z
    .union([z.date(), z.null()])
    .refine((date) => date !== null, { message: "Service Date is required" }),
  SERVICE_END_DATE: z
    .union([z.date(), z.null()])
    .refine((date) => date !== null, {
      message: "Service End Date is required",
    }),
  JOB_START_TIME: z.string().min(1, { message: "Start time is required" }),
  JOB_END_TIME: z.string().min(1, { message: "End time is required" }),
  JOB_SITE_ADDRESS: z
    .string()
    .min(1, { message: "Job site address is required" }),

  REQUIRED_APPLICANTS: z.string().optional(),
  JOB_NAME: z.string().optional(),

  SERVICE_TYPE: z.string().min(1, { message: "To is Required" }),
  JOB_SERVICE_DURATION: z.string().min(1, "Field is required"),
});

// export const jobPostedSchema = z.object({
//     PAY_AMOUNT_FROM: z.string().min(1, { message: "From is Required" }),
//     PAY_AMOUNT_TO: z.string().min(1, { message: "To is Required" }),
//     SERVICE_DATE: z.union([z.date(), z.literal(null)]).refine(date => {
//         if (date === null) return false;
//         return !isNaN(date.getTime());
//     }, { message: "Service Date is required" }),
//     JOB_IN_TIME: z.union([z.date(), z.literal(null)]).refine(date => {
//         if (date === null) return false;
//         return !isNaN(date.getTime());
//     }, { message: "From Time is required" }),

//     JOB_OUT_TIME: z.union([z.date(), z.literal(null)]).refine(date => {
//         if (date === null) return false;
//         return !isNaN(date.getTime());
//     }, { message: "To Time is required" }),

//     // SERVICE_TYPE: z.string().min(1, { message: "Service Type is Required" }),
//     // JOB_TYPE: z.string().min(1, { message: "Job Type is Required" }),

//     // One Time or Recurring specific fields
//     // ...(z.object({
//     //     JOB_SERVICE_DURATION: z.string().min(1, { message: "Field is required" }),
//     //     JOB_SERVICE_WITH_DAY_TIME: z.array(
//     //         z.object({
//     //             JOB_DAY: z.string().min(1, { message: "Day must not be empty" }),
//     //             JOB_IN_TIME: z.string().min(1, { message: "From must not be empty" }),
//     //             JOB_OUT_TIME: z.string().min(1, { message: "To must not be empty" }),
//     //             JOB_ID: z.string().min(1, { message: "ID must not be empty" }),
//     //         })
//     //     ).min(1, { message: "At least one schedule day must be selected" }),
//     // })).refine(data => data.SERVICE_TYPE !== "One Time"),
// });

export const jobPostedSchema = z
  .object({
    SERVICE_TYPE: z.string(),
    PAY_AMOUNT_FROM: z.string().min(1, { message: "From is Required" }),
    PAY_AMOUNT_TO: z.string().min(1, { message: "To is Required" }),

    SERVICE_DATE: z.union([z.date(), z.literal(null)]).refine(
      (date) => {
        if (date === null) return false;
        return !isNaN(date.getTime());
      },
      { message: "Service Date is required" },
    ),

    JOB_IN_TIME: z.union([z.date(), z.literal(null)]).refine(
      (date) => {
        if (date === null) return false;
        return !isNaN(date.getTime());
      },
      { message: "From Time is required" },
    ),

    JOB_OUT_TIME: z.union([z.date(), z.literal(null)]).refine(
      (date) => {
        if (date === null) return false;
        return !isNaN(date.getTime());
      },
      { message: "To Time is required" },
    ),
  })
  .refine((data) => data.SERVICE_TYPE !== "Recurring", {
    message: "Validation only applies when SERVICE_TYPE is not 'Recurring'",
  });

export const PreferredWorkTypeSchema = z.object({
  PREFERRED_WORK_TYPE: z
    .array(z.string())
    .min(1, "At least one work type must be selected"),
});

export const AddExperienceOnBoardSchema = z
  .object({
    EXP_JOBROLE_ID: z.string().min(1, "Job role is required"), // ID validation
    EXP_JOBROLE_NAME: z.string().min(1, "Job role is required"), // Name validation
    EXP_SPECIALTY_ID: z.string().min(1, "Specialty is required"), // ID validation
    EXP_SPECIALTY_NAME: z.string().min(1, "Specialty is required"), // Name validation
    EXP_FACILITY_NAME: z.string().min(1, "Facility name is required"),

    EXP_FROM_DATE: z.string().min(1, "From date is required"),
    EXP_TO_DATE: z.string().nullable().optional(),
    // EXP_FROM_DATE: z.union([z.date(), z.literal(null)]).refine(date => {
    //     if (date === null) return false; // If null, return false for the "required" message
    //     return !isNaN(date.getTime()); // Validate if the date is not invalid
    // }, {
    //     message: "From Date is required",
    // }),
    // EXP_TO_DATE: z.union([z.date(), z.literal(null)]).refine(date => {
    //     if (date === null) return false; // If null, return false for the "required" message
    //     return !isNaN(date.getTime()); // Validate if the date is not invalid
    // }, {
    //     message: "To Date is required",
    // })
  })
  .refine(
    (data) => {
      if (data.EXP_FROM_DATE && data.EXP_TO_DATE) {
        return data.EXP_TO_DATE > data.EXP_FROM_DATE;
      }
      return true;
    },
    {
      message: "To Date must be after From Date",
      path: ["CROSS_FIELD_ERROR"],
    },
  );

export const ExperienceOnBoardSchema = z
  .object({
    EXP_JOBROLE_ID: z.string().min(1, "Job role is required"), // ID validation
    EXP_JOBROLE_NAME: z.string().min(1, "Job role is required"), // Name validation
    EXP_SPECIALTY_ID: z.string().min(1, "Specialty is required"), // ID validation
    EXP_SPECIALTY_NAME: z.string().min(1, "Specialty is required"), // Name validation
    EXP_FACILITY_NAME: z.string().min(1, "Facility name is required"),
    EXP_TYPE: z.string().min(1, "Experience Type is required"),
    EXP_STREET_ADDRESS: z
      .string()
      .min(1, "Experience Street address is required"),
    EXP_FROM_DATE: z.string().min(1, "From date is required"),
    EXP_TO_DATE: z.string().nullable().optional().or(z.literal("")), // Allow empty string or null
    // EXP_FROM_DATE: z.union([z.date(), z.literal(null)]).refine(date => {
    //     if (date === null) return false; // If null, return false for the "required" message
    //     return !isNaN(date.getTime()); // Validate if the date is not invalid
    // }, {
    //     message: "From Date is required",
    // }),
    // EXP_TO_DATE: z.union([z.date(), z.literal(null)]).refine(date => {
    //     if (date === null) return false; // If null, return false for the "required" message
    //     return !isNaN(date.getTime()); // Validate if the date is not invalid
    // }, {
    //     message: "To Date is required",
    // })
  })
  .refine(
    (data) => {
      // Check if both dates exist and validate relationship
      if (data.EXP_FROM_DATE && data.EXP_TO_DATE && data.EXP_TO_DATE !== "") {
        // To Date must be equal to or after From Date
        return data.EXP_TO_DATE >= data.EXP_FROM_DATE;
      }
      return true; // If To Date is null/empty (currently working), skip this check
    },
    {
      message: "To Date must be equal to or after From Date",
      path: ["EXP_TO_DATE"],
    },
  );

export const ExperienceOnBoardSchema1 = z
  .object({
    REFERENCE_FACILITY_NAME: z.string().min(1, "Facility name is required"), // ID validation
    REFERENCE_FIRST_NAME: z.string().min(1, "First name is required"), // Name validation
    REFERENCE_LAST_NAME: z.string().min(1, "Last name is required"), // ID validation
    REFERENCE_EMAIL_ADDRESS: z
      .string()
      .min(1, "Email is required")
      .email("Invalid email address"),
    REFERENCE_WORKING_FROM_DATE: z
      .union([z.date(), z.custom((val) => dayjs.isDayjs(val))])
      .transform((val) => (dayjs.isDayjs(val) ? val.toDate() : val))
      .refine((date) => !isNaN(date.getTime()), "From date is required"),
    REFERENCE_WORKING_TO_DATE: z
      .union([z.date(), z.custom((val) => dayjs.isDayjs(val))])
      .transform((val) => (dayjs.isDayjs(val) ? val.toDate() : val))
      .refine((date) => !isNaN(date.getTime()), "To Date is required"),
    // EXP_FROM_DATE: z.union([z.date(), z.literal(null)]).refine(date => {
    //     if (date === null) return false; // If null, return false for the "required" message
    //     return !isNaN(date.getTime()); // Validate if the date is not invalid
    // }, {
    //     message: "From Date is required",
    // }),
    // EXP_TO_DATE: z.union([z.date(), z.literal(null)]).refine(date => {
    //     if (date === null) return false; // If null, return false for the "required" message
    //     return !isNaN(date.getTime()); // Validate if the date is not invalid
    // }, {
    //     message: "To Date is required",
    // })
  })
  .refine(
    (data) => {
      // Check if both dates are not the same
      if (data.REFERENCE_WORKING_FROM_DATE && data.REFERENCE_WORKING_TO_DATE) {
        return (
          data.REFERENCE_WORKING_FROM_DATE.getTime() !==
          data.REFERENCE_WORKING_TO_DATE.getTime()
        );
      }
      return true; // If one or both dates are null, skip this check
    },
    {
      message: "From Date and To Date must not be the same",
      path: ["CROSS_FIELD_ERROR"],
    },
  );

export const AddReferenceSchema = z.object({
  REFERENCE_FIRST_NAME: z
    .string()
    .min(1, { message: "First Name is Required" }),
  REFERENCE_LAST_NAME: z.string().min(1, { message: "Last Name is Required" }),
  REFERENCE_EMAIL_ADDRESS: z.string().email("Email is required"),
  REFERENCE_FACILITY_NAME: z.string().min(1, "Facility name is required"),
  REFERENCE_WORKING_FROM_DATE: z
    .date()
    .refine((date) => !isNaN(date.getTime()), "From date is required"),
  REFERENCE_WORKING_TO_DATE: z
    .date()
    .refine((date) => !isNaN(date.getTime()), "To Date is required"),
  REFERENCE_CONSENT: z.boolean().optional(),
});

const AddressComponentSchema = z.object({
  STREET: z.string().min(1, "Street is required"),
  // STATE: z.string().min(1, "State is required"),
  // COUNTRY: z.string().min(1, "Country is required"),
  // ZIPCODE: z.string().min(1, "ZipCode is required"), // You can add more specific validation like length or regex
  // CITY: z.string().min(1, "City is required"),
});

export const LocationOnBoard = z.object({
  STREET_ADDRESS: z.string().min(1, "Street Address is required"), // Assuming you are validating this as well
  ADDRESS_COMPONENTS: AddressComponentSchema,
});
export const addressComponentValidation = z.object({
  street: z.string().min(1, "Street is required"),
  state: z.string().min(1, "State is required"),
  country: z.string().min(1, "Country is required"),
  zipcode: z.string().min(1, "ZipCode is required"), // You can add more specific validation like length or regex
  city: z.string().min(1, "City is required"),
});
export const addAnotherLocationFacility = z.object({
  facilityName: z.string().min(1, "Facility Name is required"), // Assuming you are validating this as well
  streetAddress: z.string().min(1, "Street Address is required"), // Assuming you are validating this as well
  address_components: addressComponentValidation,
});
export const EmailAndPassword = z.object({
  PASSWORD: z
    .string()
    .min(7, "Password should be more than 6 characters") // Ensuring PASSWORD is more than 4 characters
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter") // Ensuring at least one uppercase letter
    .regex(/[a-z]/, "Password must contain at least one lowercase letter") // Ensuring at least one lowercase letter
    .regex(/\d/, "Password must contain at least one digit") // Ensuring at least one digit
    .regex(/[^a-zA-Z0-9]/, "Password must contain at least one special symbol"), // Ensuring at least one special symbol

  EMAIL_ADDRESS: z.string().min(1, "Email is required"),
});

export const Email = z.object({
  EMAIL_ADDRESS: z.string().email("Invalid Email Address"), // Ensuring valid email address
});

export const AddFacilitySchema = z.object({
  STREET_ADDRESS: z.string().min(1, "Street Address is required"),
  ADDRESS_COMPONENTS: AddressComponentSchema,
  FACILITY_NAME: z.string().min(1, { message: "Facility Name is Required" }),
  // EIN: z.string().min(1, "EIN is required").regex(/^\d{2}-\d{7}$/, "Invalid EIN format. Must be in the format XX-XXXXXXX."),
  FACILITY_PHONE_NUMBER: z
    .string()
    .min(1, { message: "Phone Number is Required" })
    .refine((val) => validateUSPhoneNumber(val), {
      message: "Invalid Phone Number",
    }),
  WEBSITE_NAME: z.string().optional(),
});

export const addRole = z.object({
  ROLE_NAME: z.string().min(1, "Role Name is required"),
  USER_TYPE: z.string().min(1, "User Type is required"),
  DESCRIPTION: z.string().optional(),
  STATUS: z.string().optional(),
});
export const addUserSchema = z.object({
  // EMAIL_ADDRESS: z.string().min(1, "Email Address is required"),
  EMAIL_ADDRESS: z
    .string()
    .min(1, "Email Address is required")
    .email("Enter a valid email address"),
  ROLE_ID: z.string().min(1, "Role is required"),
});
export const jobRoleFacilitySchema = z.object({
  JOBROLES_LIST: z
    .array(
      z.object({
        id: z.string().uuid(), // Ensure the id is a valid UUID (adjust if needed)
        name: z.string().min(1, "Job role must not be empty"), // Ensure the name is a non-empty string
      }),
    )
    .min(1, "At least one Job role must be selected"),
});

export const jobRoleFacilitySchema2 = z.object({
  JOBROLES_LIST: z
    .array(
      z.object({
        // Ensure the id is a valid UUID (adjust if needed)
        name: z.string().min(1, "Job role must not be empty"), // Ensure the name is a non-empty string
      }),
    )
    .min(1, "At least one Job role must be selected"),
});
export const facilitySignupFirstStepSchema = z.object({
  FACILITY_NAME: z.string().min(1, { message: "Facility Name is Required" }),
  EMAIL_ADDRESS: z
    .string()
    .min(1, "Email is required")
    .email("Invalid Email Address"),
});

export const profileEditSchema = z.object({
  FIRST_NAME: z
    .string()
    .min(1, "First Name is required")
    .max(50, "First Name is too long"),
  LAST_NAME: z
    .string()
    .min(1, "Last Name is required")
    .max(50, "Last Name is too long"),
  DATE_OF_BIRTH: z
    .string({
      required_error: "Date of Birth is required",
    })
    .min(1, "Date of Birth is required"),

  // DATE_OF_BIRTH: z.date().optional().refine(date => !isNaN(date.getTime()), "Date of Birth is required"),
  SSN: z
    .string()
    .min(1, "SSN Can't be Blank")
    .min(9, "SSN must be at least 9 characters")
    .max(11, "SSN must be at most 11 characters"),
  GENDER: z.string().optional(),
  ETHNICITY: z.string().optional(),
  EMAIL_ADDRESS: z.string().optional(),
  PHONE_NUMBER: z.string().optional(),
});
export const ClinicianResumeUploadSchmea = (uploadFile) =>
  z.object({
    FILE_NAME: z.any().superRefine((value, ctx) => {
      // Check if the input is not a File
      if (!uploadFile) {
        if (!(value instanceof File)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Please provide a valid file",
          });
          return;
        }

        // Check the file size
        if (value.size > 5 * 1024 * 1024) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "File size must be less than 5MB",
          });
        }

        // Check the file type
        const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];
        if (!allowedTypes.includes(value.type)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Only PDF, JPEG, and PNG files are allowed",
          });
        }
      }
    }),
  });
export const TrainingCertificateSchema = (uploadFile) =>
  z.object({
    trainingCertificateName: z
      .string()
      .min(1, { message: "Training Certificate Name is required" }),
    requirementType: z.string().min(1, { message: "Requirement is required" }),
    trainingImage: z.any().superRefine((value, ctx) => {
      // Check if the input is not a File
      if (!uploadFile) {
        if (!(value instanceof File)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Please provide a valid file",
          });
          return;
        }

        // Check the file size
        if (value.size > 5 * 1024 * 1024) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "File size must be less than 5MB",
          });
        }

        // Check the file type
        const allowedTypes = ["image/jpeg", "image/png"];
        if (!allowedTypes.includes(value.type)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Only JPEG, and PNG files are allowed",
          });
        }
      }
    }),
  });
export const ClinicianLicenseSchema = (uploadFile) =>
  z
    .object({
      LICENSE_FILE: z.any().superRefine((value, ctx) => {
        // Check if the input is not a File
        if (!uploadFile) {
          if (!(value instanceof File)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Please provide a valid file",
            });
            return;
          }

          // Check the file size
          if (value.size > 5 * 1024 * 1024) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "File size must be less than 5MB",
            });
          }

          // Check the file type
          const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];
          if (!allowedTypes.includes(value.type)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Only PDF, JPEG, and PNG files are allowed",
            });
          }
        }
      }),

      LICENSE_STATE: z.string().min(1, "State is required"),
      SET_AS_PRIMARY: z.number().optional(),
      // LICENSE_FILE: z.string().optional(),
      LICENSE_TYPE: z.string().min(1, "License Type is required"),
      LICENSE_NUMBER: z.string().min(1, "License Number is required"),
      LICENSE_ISSUE_DATE: z.union([z.date(), z.literal(null)]).refine(
        (date) => {
          if (date === null) return false; // If null, return false for the "required" message
          return !isNaN(date.getTime()); // Validate if the date is not invalid
        },
        {
          message: "Issue Date is required",
        },
      ),
      LICENSE_EXPIRE_DATE: z.union([z.date(), z.literal(null)]).refine(
        (date) => {
          if (date === null) return false; // If null, return false for the "required" message
          return !isNaN(date.getTime()); // Validate if the date is not invalid
        },
        {
          message: "Expiration Date is required",
        },
      ),
      // LICENSE_EXPIRE_DATE: z.string().min(1, "Expiration Date is required"),
    })
    .refine(
      (data) => {
        // Check if both dates are not the same
        if (data.LICENSE_ISSUE_DATE && data.LICENSE_EXPIRE_DATE) {
          return (
            data.LICENSE_ISSUE_DATE.getTime() !==
            data.LICENSE_EXPIRE_DATE.getTime()
          );
        }
        return true; // If one or both dates are null, skip this check
      },
      {
        message: "Issue Date and Expiration Date must not be the same",
        path: ["CROSS_FIELD_ERROR"],
      },
    );

export const shiftScheduleSchema = z.object({
  facilityName: z.string().min(1, "Facility Name is required"),
  jobRoleID: z.string().min(1, "Job Role is required"),
  // jobID: z.string().min(1, "Job ID is required"),
  jobID: z.string().optional(),
  specialityID: z.string().min(1, "Specialty is required"),
  // clinicianID: z.string().min(1, "Clinician is required"),
  clinicianID: z.string().optional(),
  // jobDate: z.union([z.date(), z.literal(null)]).refine(date => {
  //     if (date === null) return false; // If null, return false for the "required" message
  //     return !isNaN(date.getTime()); // Validate if the date is not invalid
  // }, {
  //     message: "Job Date is required",
  // }),
  jobDate: z
    .union([z.date(), z.string(), z.literal(null), z.undefined()])
    .refine(
      (date) => {
        if (date === null || date === undefined || date === "") return true; // Optional
        const parsedDate = new Date(date);
        return !isNaN(parsedDate.getTime());
      },
      {
        message: "Invalid Start Date",
      },
    ),
  startDate: z
    .union([z.date(), z.string(), z.literal(null), z.undefined()])
    .refine(
      (date) => {
        if (date === null || date === undefined || date === "") return true; // Optional
        const parsedDate = new Date(date);
        return !isNaN(parsedDate.getTime());
      },
      {
        message: "Invalid Start Date",
      },
    ),
  endDate: z
    .union([z.date(), z.string(), z.literal(null), z.undefined()])
    .refine(
      (date) => {
        if (date === null || date === undefined || date === "") return true; // Optional
        const parsedDate = new Date(date);
        return !isNaN(parsedDate.getTime());
      },
      {
        message: "Invalid Start Date",
      },
    ),
  defaultShift: z.string().optional(),
  shift: z.string(), // String is appropriate

  // startTime: z.string().refine(val => val.match(/^\d{1,2}:\d{2}\s?(AM|PM)$/i), {
  //     message: "Start Time must be in hh:mm AM/PM format"
  //   }),
  // endTime: z.string().refine(val => val.match(/^\d{1,2}:\d{2}\s?(AM|PM)$/i), {
  //     message: "Start Time must be in hh:mm AM/PM format"
  //   }),

  startTime: z
    .string()
    .refine((val) => val.match(/^\d{1,2}:\d{2}\s?(AM|PM)$/i), {
      message: "Start Time must be in hh:mm AM/PM format",
    }),
  endTime: z.string().refine((val) => val.match(/^\d{1,2}:\d{2}\s?(AM|PM)$/i), {
    message: "End Time must be in hh:mm AM/PM format",
  }),

  shiftQuantity: z.string().min(1, "Quantity is required"), // Should be numeric if representing count
  specificDates: z.string().min(1, "Specific Date is required").optional(), // Array of dates if multiple dates are expected
  type: z.string().min(1, "Shift Type is required"),
  payoutType: z.string().optional(),
  floor: z.string().optional(),
  notes: z.string().optional(),
  requestedTemps: z.array(z.any()).optional().nullable(),
});

export const editShiftScheduleSchema = z.object({
  facilityName: z.string().min(1, "Facility Name is required"),
  jobRoleID: z.string().min(1, "Job Role is required"),
  specialityID: z.string().optional(),
  jobDate: z.string().optional().nullable(),
  defaultShift: z.string().optional(),
  shift: z.string().optional(),
  startTime: z.string().optional(),
  endTime: z.string().optional(),
  shiftQuantity: z.string().min(1, "Quantity is required"),
  specificDates: z.string().optional(),
  type: z.string().min(1, "Shift Type is required"),
  payoutType: z.string().optional(),
  floor: z.string().optional(),
  notes: z.string().optional(),
  requestedTemps: z.array(z.any()).optional().nullable(),
});

export const bankDetailSchema = z.object({
  bankName: z.string().min(2, "Bank Name is required"),
  accountHolderName: z.string().min(2, "Bank Name is required"),
  accountType: z.string().min(2, "Account Type is required"),
  branchAddress: z.string().min(2, "Branch Address is required"),
  routingNumber: z
    .string()
    .length(9, "Routing Number must be exactly 9 digits")
    .regex(/^\d{9}$/, "Routing Number must contain only digits"),
  accountNumber: z
    .string()
    .min(5, "Account number must be at least 5 digits")
    .max(17, "Account number must not exceed 17 digits")
    .regex(/^\d{5,17}$/, "Account number must contain only digits"),

  FILE: z
    .instanceof(File)
    .refine((file) => file.size <= 5 * 1024 * 1024, {
      message: "File size must be less than 10MB",
    })
    .refine(
      (file) =>
        ["application/pdf", "image/jpeg", "image/png"].includes(file.type),
      {
        message: "Only PDF, JPEG, and PNG files are allowed",
      },
    )
    .optional(),
});

export const QualificationSchema = z
  .object({
    DEGREE_NAME: z.string().min(1, "Degree name is required"),
    INSTITUTE_NAME: z.string().min(1, "Institute name is required"),
    FROM_YEAR: z.date({
      required_error: "From date is required",
      invalid_type_error: "Please select a valid date",
    }),
    TO_YEAR: z.date({
      required_error: "To date is required",
      invalid_type_error: "Please select a valid date",
    }),
  })
  .refine((data) => data.TO_YEAR > data.FROM_YEAR, {
    message: "To date must be after From date",
    path: ["TO_YEAR"],
  });
